import React from 'react';
import { Route, Switch, useLocation } from 'react-router';
import {
    ACTION,
    FORGOT_YOUR_PASSWORD,
    FORGOT_YOUR_PASSWORD_CONFIRM,
    HEALTH,
    JOIN,
    SIGN_IN,
    SIGN_UP,
    UNAUTHORIZED
} from 'config/routes';
import { PrivateRoute } from 'lib/PrivateRoute';
import ForgotYourPassword from 'pages/forgot-your-password';
import ForgotYourPasswordConfirm from 'pages/forgot-your-password/confirm';
import Health from 'pages/health';
import Join from 'pages/join';
import SignIn from 'pages/sign-in';
import SignUp from 'pages/sign-up';
import Unathorized from 'pages/unauthorized';
import { Throbber } from 'ui/Throbber';
import ActionPage from './action';
import Authorized from './authorized';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store/store';
import { getPublicAndPrivateSettings, resetPublicSettings } from 'store/settings/settingsActions';
import { Userpilot } from 'userpilot';
import { auth } from 'lib/firebase/firebase';

interface RootProps {
    isFirebaseLoaded: boolean;
}

const RootPure: React.FC<RootProps> = ({ isFirebaseLoaded }) => {
    const dispatch = useDispatch();
    const { tenant, claims } = useSelector((state: ApplicationState) => state.auth);
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const location = useLocation();

    React.useEffect(() => {
        if (isFirebaseLoaded && process.env.NODE_ENV === 'production') {
            // For userpilot to properly work we need to send the updates on the page change
            Userpilot.reload();
        }
    }, [isFirebaseLoaded, location]);

    React.useEffect(() => {
        if (isFirebaseLoaded && tenant?._id) {
            getPublicAndPrivateSettings(claims)(dispatch);
        }
        return () => {
            resetPublicSettings()(dispatch);
        };
    }, [claims, dispatch, isFirebaseLoaded, tenant?._id]);
    // Refresh tenant/user information
    React.useEffect(() => {
        const identifyUserpilot = async () => {
            const currentUser = auth().currentUser;
            if (currentUser) {
                const tokenResult = await currentUser.getIdTokenResult();
                const { email, name } = tokenResult.claims;
                if (process.env.NODE_ENV === 'production' && isFirebaseLoaded && settings && claims) {
                    Userpilot.identify(currentUser.uid, {
                        name,
                        email,
                        rId: claims.roleId,
                        created_at: currentUser.metadata.creationTime,
                        company: {
                            id: claims.tenantId,
                            name: tenant?.title,
                            premiumLoyaltyEnabled: !!settings?.premiumLoyaltyEnabled,
                            scheduledNotificationsEnabled: !!settings?.scheduledNotificationsEnabled,
                            triggeredNotificationsEnabled: !!settings?.triggeredNotificationsEnabled
                        }
                    });
                }
            }
        };
        try {
            identifyUserpilot();
        } catch {
            // do nothing
        }
    }, [
        claims,
        claims?.roleId,
        claims?.tenantId,
        isFirebaseLoaded,
        settings,
        settings?.premiumLoyaltyEnabled,
        settings?.scheduledNotificationsEnabled,
        settings?.triggeredNotificationsEnabled,
        tenant?.title
    ]);
    if (!isFirebaseLoaded) {
        return <Throbber />;
    }
    return (
        <Switch>
            <Route exact path={ACTION} component={ActionPage} />
            <Route exact path={HEALTH} component={Health} />
            <Route exact path={UNAUTHORIZED} component={Unathorized} />
            <Route exact path={SIGN_IN} component={SignIn} />
            <Route exact path={SIGN_UP} component={SignUp} />
            <Route exact path={FORGOT_YOUR_PASSWORD} component={ForgotYourPassword} />
            <Route exact path={FORGOT_YOUR_PASSWORD_CONFIRM} component={ForgotYourPasswordConfirm} />
            <Route exact path={JOIN} component={Join} />
            <PrivateRoute path="/" component={Authorized} />
        </Switch>
    );
};

export const Root = React.memo(RootPure);
